import React, { ReactNode } from "react";
import { Box, BoxProps, Typography, useTheme } from "@mui/material";
import { defaultBaseColor } from "../constants";

interface CenteredContainerProps extends Omit<BoxProps, "title"> {
    children: ReactNode;
    title?: string | ReactNode;
    logoUrl?: string;
    backgroundColor: string;
}

function CenteredContainer({ children, title, backgroundColor, sx, logoUrl, ...props }: CenteredContainerProps) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                minHeight: "100vh", // Full viewport height
                display: "flex",
                justifyContent: "center", // Horizontal centering
                alignItems: "center", // Vertical centering
                p: 2, // Padding for mobile edges
                backgroundColor,
                ...sx, // Apply custom styles
            }}
            {...props} // Spread additional props
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center", // Center title and content
                    gap: 2, // Space between title and children
                    width: "100%",
                }}
            >
                {logoUrl && <Box component="img" src={logoUrl} sx={{ maxWidth: "100%" }} />}
                {title && !logoUrl && (
                    <Typography
                        variant="h5"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontFamily: "Glacial",
                            color: theme.palette.getContrastText(backgroundColor),
                        }}
                    >
                        {title}
                    </Typography>
                )}
                <Box
                    sx={{
                        bgcolor: "grey.200",
                        // width: { xs: '100%', sm: 400 },
                        borderRadius: 1,
                        boxShadow: 1,
                        p: 2, // Padding for content
                    }}
                >
                    {title && logoUrl && (
                        <Typography variant="h5" component="h1" mb={2} sx={{ fontFamily: "Glacial" }}>
                            {title}
                        </Typography>
                    )}
                    {children}
                </Box>
            </Box>
        </Box>
    );
}

type LoggedOutPageProps = {
    title: ReactNode;
    children?: Array<ReactNode> | ReactNode;
};

const LoggedOutPage = ({ title = "Heimdall", children }: LoggedOutPageProps) => {
    const logoUrl = window.publicLogoUrl || window.defaultTheme["login-logo-image"];
    const backgroundColor =
        window.publicBackgroundColor || window.defaultTheme["login-background-color"] || defaultBaseColor;

    return (
        <CenteredContainer title={title} backgroundColor={backgroundColor} logoUrl={logoUrl}>
            {children}
        </CenteredContainer>
    );
};

export default LoggedOutPage;
